import type { SVGProps } from 'react';

/** The prototype's inline icons, 16×16 stroke glyphs. Decorative unless given a title. */
type P = SVGProps<SVGSVGElement>;
const base = { viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', 'aria-hidden': true } as const;

export const Icon = {
  Eye: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <path d="M1.5 8s2.4-4.5 6.5-4.5S14.5 8 14.5 8s-2.4 4.5-6.5 4.5S1.5 8 1.5 8z" />
      <circle cx="8" cy="8" r="2" />
    </svg>
  ),
  Help: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <circle cx="8" cy="8" r="6" />
      <path d="M6.3 6.2a1.8 1.8 0 0 1 3.5.6c0 1.2-1.8 1.5-1.8 2.6" />
      <circle cx="8" cy="11.3" r=".45" fill="currentColor" stroke="none" />
    </svg>
  ),
  Feedback: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" />
      <path d="M5 6.2h6M5 8.2h4" />
    </svg>
  ),
  /** Collapse the rail; mirrored to expand. */
  RailToggle: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
      <path d="M6 2.5v11" />
      <path d="M11 6 9 8l2 2" />
    </svg>
  ),
  Dashboard: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <rect x="2" y="2" width="5" height="5" rx="1" />
      <rect x="9" y="2" width="5" height="5" rx="1" />
      <rect x="2" y="9" width="5" height="5" rx="1" />
      <rect x="9" y="9" width="5" height="5" rx="1" />
    </svg>
  ),
  Cases: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <path d="M3 2h7l3 3v9H3z" />
      <path d="M9.5 2v3.5H13" />
    </svg>
  ),
  Directory: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <circle cx="8" cy="5" r="2.4" />
      <path d="M3 13.5c0-2.5 2.2-4 5-4s5 1.5 5 4" />
    </svg>
  ),
  Panels: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <circle cx="5.5" cy="5" r="2" />
      <circle cx="11" cy="6.5" r="1.7" />
      <path d="M2 13c0-2 1.6-3.2 3.5-3.2S9 11 9 13" />
      <path d="M9.8 13c0-1.6 1-2.6 2.4-2.6S14.6 11.4 14.6 13" />
    </svg>
  ),
  Consultations: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <rect x="1.8" y="4" width="9" height="8" rx="1.5" />
      <path d="M10.8 7.4L14.2 5.4v5.2l-3.4-2z" />
    </svg>
  ),
  Payables: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <path d="M2 5.5h12v8H2z" />
      <path d="M2 5.5L8 2l6 3.5" />
      <path d="M5 9.5h6" />
    </svg>
  ),
  Reports: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <path d="M2.5 13.5V7" />
      <path d="M6.5 13.5V3.5" />
      <path d="M10.5 13.5V9" />
      <path d="M14 13.5V5.5" />
    </svg>
  ),
  Pools: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <circle cx="8" cy="8" r="6.2" />
      <circle cx="6" cy="7" r="1.3" />
      <circle cx="10" cy="7" r="1.3" />
      <path d="M4.6 11.2c.4-1 1.2-1.6 2.2-1.6M11.4 11.2c-.4-1-1.2-1.6-2.2-1.6" />
    </svg>
  ),
  Customers: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <rect x="2" y="3" width="12" height="10" rx="1.5" />
      <path d="M2 6.5h12" />
    </svg>
  ),
  Config: (p: P) => (
    <svg {...base} strokeWidth="1.4" {...p}>
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 1.6v1.8M8 12.6v1.8M14.4 8h-1.8M3.4 8H1.6M12.5 3.5l-1.3 1.3M4.8 11.2l-1.3 1.3M12.5 12.5l-1.3-1.3M4.8 4.8L3.5 3.5" />
    </svg>
  ),
  Search: (p: P) => (
    <svg {...base} width="14" height="14" strokeWidth="1.5" {...p}>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
  ),
  Plus: (p: P) => (
    <svg {...base} width="13" height="13" strokeWidth="1.8" {...p}>
      <path d="M8 3v10M3 8h10" />
    </svg>
  ),
  ChevronDown: (p: P) => (
    <svg {...base} width="12" height="12" strokeWidth="1.8" {...p}>
      <path d="M4 6l4 4 4-4" />
    </svg>
  ),
  Lock: (p: P) => (
    <svg {...base} width="12" height="12" strokeWidth="1.6" {...p}>
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5 7V5a3 3 0 016 0v2" />
    </svg>
  ),
  Chat: (p: P) => (
    <svg {...base} width="13" height="13" strokeWidth="1.6" {...p}>
      <path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" />
    </svg>
  ),
  Upload: (p: P) => (
    <svg {...base} width="18" height="18" strokeWidth="1.6" {...p}>
      <path d="M8 11V3" />
      <path d="M4.5 6.5L8 3l3.5 3.5" />
      <path d="M2.5 11.5v2h11v-2" />
    </svg>
  ),
  Wand: (p: P) => (
    <svg {...base} width="13" height="13" strokeWidth="1.6" {...p}>
      <path d="M2 14l8-8" />
      <path d="M11 2l.8 1.8L13.6 4.6l-1.8.8L11 7.2l-.8-1.8L8.4 4.6l1.8-.8z" />
    </svg>
  ),
};
